import { useEffect, useState, useCallback, useMemo, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import RegisterPage from "./pages/RegisterPage";
import SurveyPage from "./pages/SurveyPage";
import ThankYouPage from "./pages/ThankYouPage";
import PrivacyPage from "./pages/PrivacyPage";
import AboutPage from "./pages/AboutPage";
import EmailVerifiedPage from "./pages/EmailVerifiedPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AuthCallback from "./pages/AuthCallback";
import DataPolicyPage from "./pages/DataPolicyPage";
import DataStoragePage from "./pages/DataStoragePage";
import ForumRulesPage from "./pages/ForumRulesPage";
import ContactPage from "./pages/ContactPage";
import ForumPage from "./pages/ForumPage";
import ClubHubPage from "./pages/ClubHubPage";
import TopicDetailPage from "./pages/TopicDetailPage";
import FanPassPage from "./pages/FanPassPage";
import ForumGatePage from "./pages/ForumGatePage";

import { LanguageProvider } from "./i18n";
import { PageLayout } from "./components/PageLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Provider Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // logout failed - clear state anyway
    }
    setUser(null);
  }, []);

  const authValue = useMemo(() => ({ user, setUser, loading, logout, checkAuth }), [user, loading, logout, checkAuth]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-pulse">
          <div className="w-16 h-16 border-4 border-[#FBBF24] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with Auth Callback Detection
const AppRouter = () => {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment (not query params) for session_id during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/survey" element={<SurveyPage />} />
      <Route path="/survey/:respondentId" element={<SurveyPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/verify-email" element={<EmailVerifiedPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/data-policy" element={<DataPolicyPage />} />
      <Route path="/data-storage" element={<DataStoragePage />} />
      <Route path="/forum-rules" element={<ForumRulesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/forum" element={<ForumPage />} />
      <Route path="/forum/topic/:topicId" element={<TopicDetailPage />} />
      <Route path="/forum/club/:slug" element={<ClubHubPage />} />
      <Route path="/forum/join" element={<ForumGatePage />} />
      <Route path="/fan-pass" element={<FanPassPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <PageLayout>
            <AppRouter />
          </PageLayout>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
